import React, { useState, useEffect } from "react";
import { Button, Typography, Box, Paper, Stack } from "@mui/material";
import LanguageSwitcher from "./LanguageSwitcher";
import PrivacyModal from "./PrivacyModal";
import { useTranslation } from "react-i18next";
import usePublicLocale from "../i18n/usePublicLocale";
import {
  initAnalytics,
  trackPageView,
  disableAnalytics,
  updateConsent,
} from "../utils/analytics";

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  usePublicLocale(["overlays"]);
  const { t } = useTranslation("overlays");

  useEffect(() => {
    try {
      const path =
        typeof window !== "undefined" &&
        window.location &&
        window.location.pathname
          ? window.location.pathname
          : "";
      // If we're on the OAuth callback route, don't block or redirect — allow the callback handler to finish
      if (path && path.startsWith("/oauth-callback")) return;
      const v = localStorage.getItem("cookie_accepted");
      // If explicitly declined, ensure analytics are disabled and don't reopen the dialog
      if (v === "0") {
        try {
          disableAnalytics();
        } catch (e) {}
        setOpen(false);
        return;
      }
      // Only show the dialog if the user hasn't made a choice yet
      if (v === null) setOpen(true);
    } catch (e) {
      setOpen(true);
    }
  }, []);

  const accept = () => {
    try {
      localStorage.setItem("cookie_accepted", "1");
    } catch (e) {}
    try {
      updateConsent(true);
    } catch (e) {}
    initAnalytics(true);
    try {
      trackPageView(
        `${window.location.pathname}${window.location.search}${window.location.hash}`,
        document.title,
      );
    } catch (error) {
      // quietly ignore analytics errors so consent UI keeps working
    }
    setOpen(false);
    try {
      window.dispatchEvent(
        new CustomEvent("cookie-accepted", { detail: true }),
      );
    } catch (e) {}
  };
  const decline = () => {
    try {
      localStorage.setItem("cookie_accepted", "0");
    } catch (e) {}
    const path =
      typeof window !== "undefined" &&
      window.location &&
      window.location.pathname
        ? window.location.pathname
        : "";
    if (path && path.startsWith("/oauth-callback")) {
      // close consent so callback can complete; token handling will continue
      setOpen(false);
      return;
    }
    // blokujemy dalej aplikację przez pozostawienie modala; ewentualnie można zrobić soft redirect
    // Clear analytics cookies and disable gtag to respect user decline
    try {
      updateConsent(false);
    } catch (e) {}
    try {
      disableAnalytics();
    } catch (e) {}
    try {
      window.dispatchEvent(
        new CustomEvent("cookie-accepted", { detail: false }),
      );
    } catch (e) {}
    setOpen(false);
  };
  // If the user declines, dispatch event to inform other parts of the app
  useEffect(() => {
    if (!open) {
      try {
        window.dispatchEvent(
          new CustomEvent("cookie-accepted", {
            detail: localStorage.getItem("cookie_accepted") === "1",
          }),
        );
      } catch (e) {}
    }
  }, [open]);

  if (!open) return null;
  return (
    <>
      <Box
        role="region"
        aria-label={t("cookieConsent.dialogTitle")}
        sx={{
          position: "fixed",
          insetInline: 0,
          bottom: 0,
          zIndex: 1400,
          p: { xs: 1.5, sm: 2 },
          pointerEvents: "none",
        }}
      >
        <Paper
          sx={{
            position: "relative",
            maxWidth: 880,
            mx: "auto",
            p: { xs: 2, sm: 2.5 },
            pointerEvents: "auto",
          }}
          elevation={8}
        >
          <Box sx={{ position: "absolute", top: 8, right: 8 }}>
            <LanguageSwitcher size="small" persistBackend={false} />
          </Box>
          <Stack spacing={1.25}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600 }}
              id="cookie-consent-title"
            >
              {t("cookieConsent.dialogTitle")}
            </Typography>
            <Typography>{t("cookieConsent.intro")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("cookieConsent.requiredNote")}
            </Typography>
            <Typography variant="body2" color="error">
              {t("cookieConsent.declineWarning")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("cookieConsent.desc")}{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setPrivacyOpen(true);
                }}
              >
                {t("cookieConsent.privacyLink")}
              </a>
              .
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={decline}>{t("cookieConsent.decline")}</Button>
              <Button variant="contained" onClick={accept}>
                {t("cookieConsent.allowAll")}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>
      <PrivacyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </>
  );
}
